import * as THREE from 'three';
import { WeaponConfig } from '../types';
import { UPGRADE } from '../constants';
import { EventEmitter } from '../core/EventEmitter';

export class Weapon {
  config: WeaponConfig;
  currentAmmo: number;
  level: number;
  isReloading = false;
  private lastFireTime = 0;
  private reloadTimer = 0;
  private events: EventEmitter;

  // Visual
  mesh: THREE.Group;
  private muzzleFlashLight: THREE.PointLight;
  private muzzleFlashTimer = 0;

  // Weapon hold positions per weapon type
  private holdPos = new THREE.Vector3(0.25, -0.2, -0.4);
  private holdRot = new THREE.Euler(0, 0, 0);
  private sprintPos = new THREE.Vector3(0.15, -0.35, -0.3);
  private sprintRot = new THREE.Euler(-0.5, 0.3, 0);
  private adsPos = new THREE.Vector3(0, -0.04, -0.12);
  private adsRot = new THREE.Euler(0, 0, 0);
  isSprinting = false;
  isADS = false;


  constructor(config: WeaponConfig, level: number, events: EventEmitter) {
    this.config = config;
    this.level = level;
    this.events = events;
    this.currentAmmo = this.getMaxAmmo();
    this.mesh = new THREE.Group();

    // Muzzle flash light (must be created before createWeaponMesh)
    this.muzzleFlashLight = new THREE.PointLight(0xffaa00, 0, 5);
    this.mesh.add(this.muzzleFlashLight);

    // Create weapon visual
    this.createWeaponMesh();
  }

  private createWeaponMesh(): void {
    // Shared material helpers
    const metalMat = (color: number, roughness = 0.3, metalness = 0.9) =>
      new THREE.MeshStandardMaterial({ color, roughness, metalness });
    const matteMat = (color: number, roughness = 0.8) =>
      new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.1 });

    const darkMetal = metalMat(0x1a1a1a);
    const gunMetal = metalMat(0x333333);
    const lightMetal = metalMat(0x555555, 0.25);

    switch (this.config.id) {
      // ──────────────────────────────────────────────
      // PISTOL
      // ──────────────────────────────────────────────
      case 'pistol': {
        // --- Slide (top part) ---
        const slide = new THREE.Mesh(
          new THREE.BoxGeometry(0.032, 0.03, 0.16),
          metalMat(0x2a2a2a, 0.25, 0.95)
        );
        slide.position.set(0, 0.015, -0.02);
        this.mesh.add(slide);

        // Slide serrations (rear)
        for (let i = 0; i < 5; i++) {
          const serration = new THREE.Mesh(
            new THREE.BoxGeometry(0.034, 0.002, 0.003),
            metalMat(0x1f1f1f, 0.2, 0.95)
          );
          serration.position.set(0, 0.031, 0.04 + i * 0.007);
          this.mesh.add(serration);
        }

        // --- Frame (lower receiver) ---
        const frame = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.025, 0.13),
          metalMat(0x3a3a3a)
        );
        frame.position.set(0, -0.012, -0.005);
        this.mesh.add(frame);

        // Dust cover / rail
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(0.028, 0.006, 0.06),
          metalMat(0x404040, 0.35)
        );
        rail.position.set(0, -0.027, -0.04);
        this.mesh.add(rail);

        // --- Barrel ---
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.008, 0.008, 0.1, 12),
          darkMetal
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(0, 0.01, -0.13);
        this.mesh.add(barrel);

        // Barrel bushing (muzzle crown)
        const muzzle = new THREE.Mesh(
          new THREE.CylinderGeometry(0.012, 0.012, 0.012, 12),
          metalMat(0x222222, 0.2)
        );
        muzzle.rotation.x = Math.PI / 2;
        muzzle.position.set(0, 0.01, -0.175);
        this.mesh.add(muzzle);

        // --- Trigger guard ---
        const guardOuter = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.004, 0.045),
          gunMetal
        );
        guardOuter.position.set(0, -0.045, -0.005);
        this.mesh.add(guardOuter);

        const guardFront = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.025, 0.004),
          gunMetal
        );
        guardFront.position.set(0, -0.032, -0.025);
        this.mesh.add(guardFront);

        // Trigger
        const trigger = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.018, 0.006),
          metalMat(0x1a1a1a, 0.4, 0.8)
        );
        trigger.position.set(0, -0.032, -0.008);
        trigger.rotation.x = -0.3;
        this.mesh.add(trigger);

        // --- Grip ---
        const grip = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.07, 0.032),
          matteMat(0x1a1a1a)
        );
        grip.position.set(0, -0.06, 0.02);
        grip.rotation.x = -0.18;
        this.mesh.add(grip);

        // Grip texture panels
        for (const side of [-1, 1]) {
          const panel = new THREE.Mesh(
            new THREE.BoxGeometry(0.002, 0.05, 0.022),
            matteMat(0x252525, 0.9)
          );
          panel.position.set(side * 0.016, -0.06, 0.02);
          panel.rotation.x = -0.18;
          this.mesh.add(panel);
        }

        // --- Magazine ---
        const mag = new THREE.Mesh(
          new THREE.BoxGeometry(0.024, 0.055, 0.025),
          metalMat(0x2a2a2a, 0.35)
        );
        mag.position.set(0, -0.065, 0.018);
        mag.rotation.x = -0.18;
        this.mesh.add(mag);

        // Magazine baseplate
        const baseplate = new THREE.Mesh(
          new THREE.BoxGeometry(0.026, 0.005, 0.027),
          metalMat(0x222222)
        );
        baseplate.position.set(0, -0.095, 0.028);
        baseplate.rotation.x = -0.18;
        this.mesh.add(baseplate);

        // --- Front sight ---
        const frontSight = new THREE.Mesh(
          new THREE.BoxGeometry(0.004, 0.01, 0.004),
          darkMetal
        );
        frontSight.position.set(0, 0.036, -0.08);
        this.mesh.add(frontSight);

        // --- Rear sight ---
        for (const side of [-1, 1]) {
          const rearSight = new THREE.Mesh(
            new THREE.BoxGeometry(0.004, 0.008, 0.004),
            darkMetal
          );
          rearSight.position.set(side * 0.01, 0.034, 0.05);
          this.mesh.add(rearSight);
        }

        // Ejection port
        const ejPort = new THREE.Mesh(
          new THREE.BoxGeometry(0.005, 0.012, 0.025),
          metalMat(0x444444, 0.2)
        );
        ejPort.position.set(0.017, 0.02, 0.0);
        this.mesh.add(ejPort);

        // Hammer
        const hammer = new THREE.Mesh(
          new THREE.BoxGeometry(0.008, 0.015, 0.008),
          gunMetal
        );
        hammer.position.set(0, 0.025, 0.06);
        this.mesh.add(hammer);

        this.muzzleFlashLight.position.set(0, 0.01, -0.19);
        break;
      }

      // ──────────────────────────────────────────────
      // SHOTGUN
      // ──────────────────────────────────────────────
      case 'shotgun': {
        const woodMat = matteMat(0x6b3a1f, 0.75);
        const darkWood = matteMat(0x4a2810, 0.8);

        // --- Receiver ---
        const receiver = new THREE.Mesh(
          new THREE.BoxGeometry(0.04, 0.045, 0.12),
          metalMat(0x2a2a2a, 0.3)
        );
        receiver.position.set(0, 0, 0);
        this.mesh.add(receiver);

        // Receiver top (rounded)
        const receiverTop = new THREE.Mesh(
          new THREE.CylinderGeometry(0.02, 0.02, 0.12, 8, 1, false, 0, Math.PI),
          metalMat(0x2e2e2e, 0.3)
        );
        receiverTop.rotation.z = Math.PI;
        receiverTop.rotation.y = Math.PI / 2;
        receiverTop.position.set(0, 0.022, 0);
        this.mesh.add(receiverTop);

        // --- Long barrel ---
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.012, 0.012, 0.38, 12),
          metalMat(0x1a1a1a, 0.2, 0.95)
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(0, 0.015, -0.25);
        this.mesh.add(barrel);

        // Barrel heat shield (ventilated)
        const heatShield = new THREE.Mesh(
          new THREE.CylinderGeometry(0.016, 0.016, 0.2, 8),
          metalMat(0x252525, 0.35)
        );
        heatShield.rotation.x = Math.PI / 2;
        heatShield.position.set(0, 0.015, -0.16);
        this.mesh.add(heatShield);

        // Heat shield vents
        for (let i = 0; i < 4; i++) {
          const vent = new THREE.Mesh(
            new THREE.BoxGeometry(0.035, 0.006, 0.008),
            metalMat(0x111111)
          );
          vent.position.set(0, 0.015, -0.1 - i * 0.04);
          this.mesh.add(vent);
        }

        // Muzzle
        const muzzle = new THREE.Mesh(
          new THREE.CylinderGeometry(0.014, 0.013, 0.015, 12),
          metalMat(0x1a1a1a, 0.2)
        );
        muzzle.rotation.x = Math.PI / 2;
        muzzle.position.set(0, 0.015, -0.445);
        this.mesh.add(muzzle);

        // --- Magazine tube (under barrel) ---
        const magTube = new THREE.Mesh(
          new THREE.CylinderGeometry(0.01, 0.01, 0.28, 10),
          metalMat(0x222222)
        );
        magTube.rotation.x = Math.PI / 2;
        magTube.position.set(0, -0.01, -0.2);
        this.mesh.add(magTube);

        // Mag tube cap
        const magCap = new THREE.Mesh(
          new THREE.SphereGeometry(0.012, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2),
          metalMat(0x222222)
        );
        magCap.rotation.x = Math.PI / 2;
        magCap.position.set(0, -0.01, -0.34);
        this.mesh.add(magCap);

        // --- Pump grip (fore-end) ---
        const pumpGrip = new THREE.Mesh(
          new THREE.BoxGeometry(0.038, 0.04, 0.09),
          woodMat
        );
        pumpGrip.position.set(0, -0.015, -0.1);
        this.mesh.add(pumpGrip);

        // Pump grip grooves
        for (let i = 0; i < 6; i++) {
          const groove = new THREE.Mesh(
            new THREE.BoxGeometry(0.04, 0.002, 0.004),
            darkWood
          );
          groove.position.set(0, -0.036, -0.07 + i * 0.013);
          this.mesh.add(groove);
        }

        // --- Stock ---
        const stockMain = new THREE.Mesh(
          new THREE.BoxGeometry(0.035, 0.05, 0.16),
          woodMat
        );
        stockMain.position.set(0, -0.005, 0.14);
        this.mesh.add(stockMain);

        // Stock rear (wider butt)
        const stockButt = new THREE.Mesh(
          new THREE.BoxGeometry(0.04, 0.065, 0.03),
          woodMat
        );
        stockButt.position.set(0, -0.005, 0.225);
        this.mesh.add(stockButt);

        // Butt pad
        const buttPad = new THREE.Mesh(
          new THREE.BoxGeometry(0.042, 0.068, 0.005),
          matteMat(0x111111, 0.9)
        );
        buttPad.position.set(0, -0.005, 0.242);
        this.mesh.add(buttPad);

        // Stock grip area taper
        const stockNeck = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.04, 0.04),
          woodMat
        );
        stockNeck.position.set(0, -0.005, 0.08);
        this.mesh.add(stockNeck);

        // --- Pistol grip ---
        const grip = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.065, 0.028),
          matteMat(0x1a1a1a)
        );
        grip.position.set(0, -0.055, 0.04);
        grip.rotation.x = -0.25;
        this.mesh.add(grip);

        // --- Trigger guard ---
        const guardBottom = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.004, 0.04),
          gunMetal
        );
        guardBottom.position.set(0, -0.042, 0.01);
        this.mesh.add(guardBottom);

        const guardFront = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.02, 0.004),
          gunMetal
        );
        guardFront.position.set(0, -0.032, -0.008);
        this.mesh.add(guardFront);

        // Trigger
        const trigger = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.016, 0.005),
          darkMetal
        );
        trigger.position.set(0, -0.03, 0.005);
        trigger.rotation.x = -0.2;
        this.mesh.add(trigger);

        // --- Front bead sight ---
        const frontBead = new THREE.Mesh(
          new THREE.SphereGeometry(0.004, 8, 8),
          metalMat(0xcccccc, 0.15, 0.95)
        );
        frontBead.position.set(0, 0.032, -0.4);
        this.mesh.add(frontBead);

        // Ejection port
        const ejPort = new THREE.Mesh(
          new THREE.BoxGeometry(0.005, 0.02, 0.035),
          metalMat(0x3a3a3a, 0.2)
        );
        ejPort.position.set(0.022, 0.01, 0.01);
        this.mesh.add(ejPort);

        this.muzzleFlashLight.position.set(0, 0.015, -0.46);
        break;
      }

      // ──────────────────────────────────────────────
      // SMG
      // ──────────────────────────────────────────────
      case 'smg': {
        // --- Upper receiver ---
        const upper = new THREE.Mesh(
          new THREE.BoxGeometry(0.035, 0.035, 0.14),
          metalMat(0x2a2a2a, 0.3)
        );
        upper.position.set(0, 0.005, 0);
        this.mesh.add(upper);

        // --- Lower receiver ---
        const lower = new THREE.Mesh(
          new THREE.BoxGeometry(0.033, 0.025, 0.1),
          metalMat(0x252525, 0.35)
        );
        lower.position.set(0, -0.018, 0.01);
        this.mesh.add(lower);

        // Top rail (picatinny)
        const topRail = new THREE.Mesh(
          new THREE.BoxGeometry(0.02, 0.005, 0.12),
          metalMat(0x333333, 0.35)
        );
        topRail.position.set(0, 0.025, -0.01);
        this.mesh.add(topRail);

        // Rail notches
        for (let i = 0; i < 10; i++) {
          const notch = new THREE.Mesh(
            new THREE.BoxGeometry(0.022, 0.003, 0.003),
            metalMat(0x222222)
          );
          notch.position.set(0, 0.029, -0.05 + i * 0.011);
          this.mesh.add(notch);
        }

        // --- Barrel (short, with shroud) ---
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.007, 0.007, 0.15, 12),
          darkMetal
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(0, 0.005, -0.14);
        this.mesh.add(barrel);

        // Barrel shroud / handguard
        const shroud = new THREE.Mesh(
          new THREE.BoxGeometry(0.032, 0.03, 0.08),
          metalMat(0x2e2e2e, 0.4)
        );
        shroud.position.set(0, 0.003, -0.1);
        this.mesh.add(shroud);

        // Shroud ventilation cuts
        for (let i = 0; i < 3; i++) {
          for (const side of [-1, 1]) {
            const vent = new THREE.Mesh(
              new THREE.BoxGeometry(0.003, 0.015, 0.012),
              metalMat(0x1a1a1a)
            );
            vent.position.set(side * 0.017, 0.003, -0.08 + i * 0.025);
            this.mesh.add(vent);
          }
        }

        // Flash hider
        const flashHider = new THREE.Mesh(
          new THREE.CylinderGeometry(0.01, 0.008, 0.025, 8),
          metalMat(0x1a1a1a, 0.2)
        );
        flashHider.rotation.x = Math.PI / 2;
        flashHider.position.set(0, 0.005, -0.225);
        this.mesh.add(flashHider);

        // --- Foregrip (vertical) ---
        const foregrip = new THREE.Mesh(
          new THREE.BoxGeometry(0.018, 0.04, 0.018),
          matteMat(0x1a1a1a)
        );
        foregrip.position.set(0, -0.033, -0.09);
        this.mesh.add(foregrip);

        // Foregrip base
        const foregrBase = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.006, 0.022),
          matteMat(0x1a1a1a)
        );
        foregrBase.position.set(0, -0.055, -0.09);
        this.mesh.add(foregrBase);

        // --- Magazine (curved) ---
        const mag = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.08, 0.02),
          metalMat(0x2a2a2a, 0.35)
        );
        mag.position.set(0, -0.07, 0.01);
        mag.rotation.x = -0.1;
        this.mesh.add(mag);

        // Magazine base
        const magBase = new THREE.Mesh(
          new THREE.BoxGeometry(0.024, 0.005, 0.022),
          metalMat(0x222222)
        );
        magBase.position.set(0, -0.112, 0.016);
        magBase.rotation.x = -0.1;
        this.mesh.add(magBase);

        // Mag well
        const magWell = new THREE.Mesh(
          new THREE.BoxGeometry(0.026, 0.01, 0.024),
          metalMat(0x282828)
        );
        magWell.position.set(0, -0.03, 0.01);
        this.mesh.add(magWell);

        // --- Pistol grip ---
        const grip = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.06, 0.028),
          matteMat(0x1a1a1a)
        );
        grip.position.set(0, -0.055, 0.045);
        grip.rotation.x = -0.3;
        this.mesh.add(grip);

        // Grip texture
        const gripTex = new THREE.Mesh(
          new THREE.BoxGeometry(0.027, 0.04, 0.002),
          matteMat(0x222222, 0.95)
        );
        gripTex.position.set(0, -0.055, 0.06);
        gripTex.rotation.x = -0.3;
        this.mesh.add(gripTex);

        // --- Trigger guard ---
        const guardBot = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.004, 0.035),
          gunMetal
        );
        guardBot.position.set(0, -0.038, 0.015);
        this.mesh.add(guardBot);

        const guardFront = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.015, 0.004),
          gunMetal
        );
        guardFront.position.set(0, -0.03, -0.0);
        this.mesh.add(guardFront);

        // Trigger
        const trigger = new THREE.Mesh(
          new THREE.BoxGeometry(0.005, 0.014, 0.005),
          darkMetal
        );
        trigger.position.set(0, -0.03, 0.01);
        trigger.rotation.x = -0.2;
        this.mesh.add(trigger);

        // --- Folding stock ---
        // Stock arm (folded alongside)
        const stockArm = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.006, 0.13),
          metalMat(0x333333, 0.3)
        );
        stockArm.position.set(0, 0.018, 0.13);
        this.mesh.add(stockArm);

        // Stock hinge
        const hinge = new THREE.Mesh(
          new THREE.CylinderGeometry(0.006, 0.006, 0.01, 8),
          gunMetal
        );
        hinge.position.set(0, 0.018, 0.065);
        this.mesh.add(hinge);

        // Stock butt plate
        const stockButt = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.035, 0.006),
          matteMat(0x222222)
        );
        stockButt.position.set(0, 0.01, 0.198);
        this.mesh.add(stockButt);

        // --- Charging handle ---
        const chHandle = new THREE.Mesh(
          new THREE.BoxGeometry(0.012, 0.008, 0.015),
          gunMetal
        );
        chHandle.position.set(0, 0.03, 0.04);
        this.mesh.add(chHandle);

        // --- Sights ---
        // Front sight
        const frontSight = new THREE.Mesh(
          new THREE.BoxGeometry(0.004, 0.012, 0.004),
          darkMetal
        );
        frontSight.position.set(0, 0.034, -0.06);
        this.mesh.add(frontSight);

        // Rear sight
        for (const side of [-1, 1]) {
          const rearSight = new THREE.Mesh(
            new THREE.BoxGeometry(0.004, 0.01, 0.004),
            darkMetal
          );
          rearSight.position.set(side * 0.008, 0.032, 0.04);
          this.mesh.add(rearSight);
        }

        this.muzzleFlashLight.position.set(0, 0.005, -0.24);
        break;
      }

      // ──────────────────────────────────────────────
      // ROCKET LAUNCHER
      // ──────────────────────────────────────────────
      case 'rocketLauncher': {
        const odGreen = metalMat(0x3a5c3a, 0.5, 0.3);
        const darkGreen = metalMat(0x2a4a2a, 0.5, 0.3);

        // --- Main launch tube ---
        const tube = new THREE.Mesh(
          new THREE.CylinderGeometry(0.035, 0.035, 0.55, 16),
          odGreen
        );
        tube.rotation.x = Math.PI / 2;
        tube.position.set(0, 0, -0.05);
        this.mesh.add(tube);

        // Tube front rim
        const frontRim = new THREE.Mesh(
          new THREE.CylinderGeometry(0.038, 0.04, 0.02, 16),
          darkGreen
        );
        frontRim.rotation.x = Math.PI / 2;
        frontRim.position.set(0, 0, -0.33);
        this.mesh.add(frontRim);

        // Tube rear rim / exhaust bell
        const rearRim = new THREE.Mesh(
          new THREE.CylinderGeometry(0.042, 0.038, 0.03, 16),
          darkGreen
        );
        rearRim.rotation.x = Math.PI / 2;
        rearRim.position.set(0, 0, 0.235);
        this.mesh.add(rearRim);

        // Exhaust cone (flared rear)
        const exhaust = new THREE.Mesh(
          new THREE.CylinderGeometry(0.05, 0.042, 0.04, 16),
          metalMat(0x2a2a2a, 0.6, 0.4)
        );
        exhaust.rotation.x = Math.PI / 2;
        exhaust.position.set(0, 0, 0.27);
        this.mesh.add(exhaust);

        // Exhaust interior (dark)
        const exhaustInner = new THREE.Mesh(
          new THREE.CylinderGeometry(0.032, 0.028, 0.03, 12),
          matteMat(0x0a0a0a)
        );
        exhaustInner.rotation.x = Math.PI / 2;
        exhaustInner.position.set(0, 0, 0.28);
        this.mesh.add(exhaustInner);

        // --- Blast shield ---
        const shield = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.07, 0.005),
          darkGreen
        );
        shield.position.set(0, 0, 0.2);
        this.mesh.add(shield);

        // --- Front grip / handle ---
        const frontGrip = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.06, 0.025),
          matteMat(0x1a1a1a)
        );
        frontGrip.position.set(0, -0.06, -0.12);
        frontGrip.rotation.x = -0.15;
        this.mesh.add(frontGrip);

        // --- Rear pistol grip ---
        const rearGrip = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.065, 0.028),
          matteMat(0x1a1a1a)
        );
        rearGrip.position.set(0, -0.065, 0.08);
        rearGrip.rotation.x = -0.3;
        this.mesh.add(rearGrip);

        // --- Trigger mechanism ---
        const triggerHousing = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.02, 0.04),
          darkGreen
        );
        triggerHousing.position.set(0, -0.035, 0.06);
        this.mesh.add(triggerHousing);

        const trigger = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.015, 0.006),
          darkMetal
        );
        trigger.position.set(0, -0.04, 0.05);
        trigger.rotation.x = -0.2;
        this.mesh.add(trigger);

        // Trigger guard
        const guardBot = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.004, 0.04),
          metalMat(0x2e4e2e, 0.45, 0.3)
        );
        guardBot.position.set(0, -0.05, 0.06);
        this.mesh.add(guardBot);

        // --- Scope mount ---
        const scopeMount = new THREE.Mesh(
          new THREE.BoxGeometry(0.015, 0.012, 0.04),
          darkGreen
        );
        scopeMount.position.set(0, 0.04, -0.04);
        this.mesh.add(scopeMount);

        // Scope tube
        const scopeTube = new THREE.Mesh(
          new THREE.CylinderGeometry(0.012, 0.012, 0.08, 10),
          metalMat(0x1a1a1a, 0.25)
        );
        scopeTube.rotation.x = Math.PI / 2;
        scopeTube.position.set(0, 0.055, -0.04);
        this.mesh.add(scopeTube);

        // Scope objective lens
        const scopeFront = new THREE.Mesh(
          new THREE.CylinderGeometry(0.015, 0.012, 0.01, 10),
          metalMat(0x1a1a1a, 0.2)
        );
        scopeFront.rotation.x = Math.PI / 2;
        scopeFront.position.set(0, 0.055, -0.085);
        this.mesh.add(scopeFront);

        // Scope lens (glass-like)
        const scopeLens = new THREE.Mesh(
          new THREE.CircleGeometry(0.014, 12),
          new THREE.MeshStandardMaterial({
            color: 0x4488cc,
            roughness: 0.05,
            metalness: 0.3,
            transparent: true,
            opacity: 0.6,
          })
        );
        scopeLens.position.set(0, 0.055, -0.0905);
        this.mesh.add(scopeLens);

        // Scope ocular lens
        const scopeRear = new THREE.Mesh(
          new THREE.CylinderGeometry(0.013, 0.012, 0.008, 10),
          metalMat(0x1a1a1a, 0.2)
        );
        scopeRear.rotation.x = Math.PI / 2;
        scopeRear.position.set(0, 0.055, 0.004);
        this.mesh.add(scopeRear);

        // --- Shoulder rest ---
        const shoulderPad = new THREE.Mesh(
          new THREE.BoxGeometry(0.05, 0.04, 0.015),
          matteMat(0x222222)
        );
        shoulderPad.position.set(0, -0.015, 0.16);
        this.mesh.add(shoulderPad);

        // --- Carrying handle ---
        const handleBase1 = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.015, 0.006),
          darkGreen
        );
        handleBase1.position.set(0, 0.04, 0.05);
        this.mesh.add(handleBase1);

        const handleBase2 = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.015, 0.006),
          darkGreen
        );
        handleBase2.position.set(0, 0.04, 0.1);
        this.mesh.add(handleBase2);

        const handleBar = new THREE.Mesh(
          new THREE.BoxGeometry(0.006, 0.006, 0.06),
          darkGreen
        );
        handleBar.position.set(0, 0.05, 0.075);
        this.mesh.add(handleBar);

        // Warning stripes on tube
        for (let i = 0; i < 2; i++) {
          const stripe = new THREE.Mesh(
            new THREE.CylinderGeometry(0.0355, 0.0355, 0.008, 16),
            metalMat(0x888833, 0.5, 0.2)
          );
          stripe.rotation.x = Math.PI / 2;
          stripe.position.set(0, 0, -0.25 + i * 0.03);
          this.mesh.add(stripe);
        }

        this.muzzleFlashLight.position.set(0, 0, -0.35);
        break;
      }

      // ──────────────────────────────────────────────
      // GRENADE
      // ──────────────────────────────────────────────
      // ──────────────────────────────────────────────
      // ASSAULT RIFLE
      // ──────────────────────────────────────────────
      case 'assaultRifle': {
        // Upper receiver
        const upperReceiver = new THREE.Mesh(
          new THREE.BoxGeometry(0.035, 0.035, 0.18),
          metalMat(0x2a2a2a, 0.3)
        );
        upperReceiver.position.set(0, 0.01, -0.02);
        this.mesh.add(upperReceiver);

        // Lower receiver
        const lowerReceiver = new THREE.Mesh(
          new THREE.BoxGeometry(0.033, 0.03, 0.12),
          metalMat(0x333333, 0.35)
        );
        lowerReceiver.position.set(0, -0.015, 0);
        this.mesh.add(lowerReceiver);

        // Barrel (long)
        const arBarrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.007, 0.007, 0.25, 8),
          darkMetal
        );
        arBarrel.rotation.x = Math.PI / 2;
        arBarrel.position.set(0, 0.015, -0.22);
        this.mesh.add(arBarrel);

        // Barrel shroud / handguard
        const handguard = new THREE.Mesh(
          new THREE.BoxGeometry(0.032, 0.028, 0.15),
          metalMat(0x3a3a3a, 0.4)
        );
        handguard.position.set(0, 0.01, -0.15);
        this.mesh.add(handguard);

        // Rail on top
        const arRail = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.005, 0.25),
          metalMat(0x444444, 0.35)
        );
        arRail.position.set(0, 0.033, -0.06);
        this.mesh.add(arRail);

        // Rail notches
        for (let i = 0; i < 10; i++) {
          const notch = new THREE.Mesh(
            new THREE.BoxGeometry(0.027, 0.002, 0.002),
            metalMat(0x333333)
          );
          notch.position.set(0, 0.037, -0.15 + i * 0.025);
          this.mesh.add(notch);
        }

        // Muzzle brake
        const muzzle = new THREE.Mesh(
          new THREE.CylinderGeometry(0.01, 0.01, 0.025, 8),
          metalMat(0x222222, 0.2)
        );
        muzzle.rotation.x = Math.PI / 2;
        muzzle.position.set(0, 0.015, -0.35);
        this.mesh.add(muzzle);

        // Stock (collapsible)
        const stockTube = new THREE.Mesh(
          new THREE.CylinderGeometry(0.008, 0.008, 0.12, 6),
          metalMat(0x444444, 0.4)
        );
        stockTube.rotation.x = Math.PI / 2;
        stockTube.position.set(0, 0.005, 0.12);
        this.mesh.add(stockTube);

        const stockPad = new THREE.Mesh(
          new THREE.BoxGeometry(0.03, 0.04, 0.015),
          matteMat(0x222222)
        );
        stockPad.position.set(0, 0.005, 0.18);
        this.mesh.add(stockPad);

        // Grip
        const arGrip = new THREE.Mesh(
          new THREE.BoxGeometry(0.025, 0.055, 0.025),
          matteMat(0x1a1a1a)
        );
        arGrip.position.set(0, -0.04, 0.03);
        arGrip.rotation.x = -0.2;
        this.mesh.add(arGrip);

        // Trigger guard
        const arGuard = new THREE.Mesh(
          new THREE.BoxGeometry(0.022, 0.003, 0.035),
          gunMetal
        );
        arGuard.position.set(0, -0.035, 0);
        this.mesh.add(arGuard);

        // Trigger
        const arTrigger = new THREE.Mesh(
          new THREE.BoxGeometry(0.005, 0.015, 0.005),
          metalMat(0x1a1a1a, 0.4, 0.8)
        );
        arTrigger.position.set(0, -0.028, 0);
        this.mesh.add(arTrigger);

        // Magazine (curved)
        const arMag = new THREE.Mesh(
          new THREE.BoxGeometry(0.02, 0.07, 0.02),
          metalMat(0x2a2a2a, 0.35)
        );
        arMag.position.set(0, -0.055, -0.02);
        arMag.rotation.x = -0.1;
        this.mesh.add(arMag);

        // Front sight
        const arFrontSight = new THREE.Mesh(
          new THREE.BoxGeometry(0.003, 0.012, 0.003),
          darkMetal
        );
        arFrontSight.position.set(0, 0.042, -0.2);
        this.mesh.add(arFrontSight);

        // Rear sight
        for (const side of [-1, 1]) {
          const rs = new THREE.Mesh(
            new THREE.BoxGeometry(0.003, 0.008, 0.003),
            darkMetal
          );
          rs.position.set(side * 0.008, 0.04, 0.04);
          this.mesh.add(rs);
        }

        this.muzzleFlashLight.position.set(0, 0.015, -0.37);
        break;
      }

      case 'grenade': {
        const grenadeGreen = metalMat(0x3a5a2a, 0.55, 0.4);

        // --- Main body (oval / egg shape using scaled sphere) ---
        const body = new THREE.Mesh(
          new THREE.SphereGeometry(0.028, 16, 12),
          grenadeGreen
        );
        body.scale.set(1, 1.35, 1);
        body.position.set(0, 0, 0);
        this.mesh.add(body);

        // Segmentation lines (horizontal)
        for (let i = -2; i <= 2; i++) {
          const ring = new THREE.Mesh(
            new THREE.TorusGeometry(0.028, 0.001, 4, 24),
            metalMat(0x2a4a1a, 0.6, 0.3)
          );
          ring.position.set(0, i * 0.012, 0);
          this.mesh.add(ring);
        }

        // Segmentation lines (vertical)
        for (let i = 0; i < 4; i++) {
          const vRing = new THREE.Mesh(
            new THREE.TorusGeometry(0.028, 0.001, 4, 24),
            metalMat(0x2a4a1a, 0.6, 0.3)
          );
          vRing.rotation.y = (i * Math.PI) / 4;
          vRing.rotation.x = Math.PI / 2;
          vRing.scale.set(1, 1.35, 1);
          vRing.position.set(0, 0, 0);
          this.mesh.add(vRing);
        }

        // --- Fuse assembly (top cap) ---
        const fuseBase = new THREE.Mesh(
          new THREE.CylinderGeometry(0.015, 0.018, 0.015, 10),
          metalMat(0x4a4a3a, 0.4, 0.6)
        );
        fuseBase.position.set(0, 0.042, 0);
        this.mesh.add(fuseBase);

        // Fuse top
        const fuseTop = new THREE.Mesh(
          new THREE.CylinderGeometry(0.012, 0.015, 0.008, 10),
          metalMat(0x555544, 0.35, 0.6)
        );
        fuseTop.position.set(0, 0.053, 0);
        this.mesh.add(fuseTop);

        // --- Safety lever (spoon) ---
        const lever = new THREE.Mesh(
          new THREE.BoxGeometry(0.008, 0.055, 0.012),
          metalMat(0x666655, 0.35, 0.7)
        );
        lever.position.set(0.005, 0.025, 0.015);
        lever.rotation.z = 0.05;
        this.mesh.add(lever);

        // Lever bend (curved part at top)
        const leverTop = new THREE.Mesh(
          new THREE.BoxGeometry(0.008, 0.008, 0.015),
          metalMat(0x666655, 0.35, 0.7)
        );
        leverTop.position.set(0.004, 0.054, 0.01);
        this.mesh.add(leverTop);

        // --- Safety pin ---
        // Pin ring
        const pinRing = new THREE.Mesh(
          new THREE.TorusGeometry(0.008, 0.0015, 6, 12),
          metalMat(0x888877, 0.3, 0.8)
        );
        pinRing.position.set(-0.015, 0.055, 0);
        pinRing.rotation.y = Math.PI / 2;
        this.mesh.add(pinRing);

        // Pin shaft
        const pinShaft = new THREE.Mesh(
          new THREE.CylinderGeometry(0.0015, 0.0015, 0.02, 6),
          metalMat(0x888877, 0.3, 0.8)
        );
        pinShaft.rotation.z = Math.PI / 2;
        pinShaft.position.set(-0.005, 0.055, 0);
        this.mesh.add(pinShaft);

        // --- Bottom cap ---
        const bottomCap = new THREE.Mesh(
          new THREE.CylinderGeometry(0.012, 0.008, 0.008, 10),
          metalMat(0x4a4a3a, 0.4, 0.6)
        );
        bottomCap.position.set(0, -0.04, 0);
        this.mesh.add(bottomCap);

        this.muzzleFlashLight.position.set(0, 0, -0.06);
        break;
      }

      // Fallback for any unknown weapon id
      default: {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.05, 0.05, 0.3),
          gunMetal
        );
        this.mesh.add(body);
        this.muzzleFlashLight.position.set(0, 0, -0.2);
        break;
      }
    }

    // Per-weapon FPS scale (different weapons feel different sizes)
    let weaponScale = 5;
    switch (this.config.id) {
      case 'pistol': weaponScale = 5; break;
      case 'shotgun': weaponScale = 4; break;
      case 'smg': weaponScale = 4.5; break;
      case 'rocketLauncher': weaponScale = 3.5; break;
      case 'assaultRifle': weaponScale = 4; break;
      case 'grenade': weaponScale = 6; break;
      default: weaponScale = 5; break;
    }
    this.mesh.scale.setScalar(weaponScale);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 999;

    // Make all weapon materials bright MeshBasicMaterial so weapon is ALWAYS visible
    // (MeshStandardMaterial requires scene lights which may not reach camera-attached objects)
    this.mesh.traverse(child => {
      child.frustumCulled = false;
      child.renderOrder = 999;
      if (child instanceof THREE.Mesh) {
        const oldMat = child.material;
        if (oldMat instanceof THREE.MeshStandardMaterial) {
          const color = oldMat.color.clone();
          // Brighten dark colors so weapon is visible
          const r = color.r, g = color.g, b = color.b;
          const brightness = r * 0.299 + g * 0.587 + b * 0.114;
          if (brightness < 0.15) {
            color.multiplyScalar(3.0); // brighten very dark parts
          } else if (brightness < 0.3) {
            color.multiplyScalar(1.8);
          }
          child.material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
          });
          oldMat.dispose();
        }
      }
    });

    // Positions: x=right, y=up, z=forward(negative)
    // Weapon should be at bottom-center-right, like a real FPS
    switch (this.config.id) {
      case 'pistol':
        // Pistol: compact, one-handed feel, right side
        this.holdPos.set(0.12, -0.09, -0.14);
        this.holdRot.set(0, -0.08, 0);
        this.sprintPos.set(0.14, -0.18, -0.06);
        this.sprintRot.set(-0.9, 0.6, 0.2);
        this.adsPos.set(0.0, -0.04, -0.12);
        this.adsRot.set(0, 0, 0);
        break;
      case 'shotgun':
        // Shotgun: big, close to center, both hands, Fortnite pump style
        this.holdPos.set(0.08, -0.06, -0.12);
        this.holdRot.set(0, -0.04, -0.02);
        this.sprintPos.set(0.12, -0.2, -0.06);
        this.sprintRot.set(-0.6, 0.35, 0.08);
        this.adsPos.set(0.0, -0.035, -0.11);
        this.adsRot.set(0, 0, 0);
        break;
      case 'smg':
        // SMG: medium, slightly tilted, compact grip
        this.holdPos.set(0.1, -0.07, -0.13);
        this.holdRot.set(0, -0.06, -0.03);
        this.sprintPos.set(0.13, -0.18, -0.06);
        this.sprintRot.set(-0.8, 0.5, 0.15);
        this.adsPos.set(0.0, -0.035, -0.11);
        this.adsRot.set(0, 0, 0);
        break;
      case 'assaultRifle':
        // AR: military hold, center-right, aimed forward
        this.holdPos.set(0.09, -0.06, -0.14);
        this.holdRot.set(0, -0.05, -0.02);
        this.sprintPos.set(0.12, -0.18, -0.07);
        this.sprintRot.set(-0.7, 0.4, 0.1);
        this.adsPos.set(0.0, -0.035, -0.12);
        this.adsRot.set(0, 0, 0);
        break;
      case 'rocketLauncher':
        // Rocket: large tube on right shoulder, angled
        this.holdPos.set(0.12, -0.04, -0.16);
        this.holdRot.set(0, -0.03, -0.05);
        this.sprintPos.set(0.14, -0.2, -0.08);
        this.sprintRot.set(-0.5, 0.3, 0.05);
        this.adsPos.set(0.02, -0.03, -0.14);
        this.adsRot.set(0, 0, 0);
        break;
      case 'grenade':
        // Grenade: held up in hand, small
        this.holdPos.set(0.1, -0.06, -0.1);
        this.holdRot.set(0, 0, 0.1);
        this.sprintPos.set(0.12, -0.15, -0.05);
        this.sprintRot.set(-0.6, 0.4, 0.2);
        this.adsPos.set(0.04, -0.04, -0.1);
        this.adsRot.set(-0.3, 0, 0);
        break;
      default:
        this.holdPos.set(0.12, -0.08, -0.14);
        this.holdRot.set(0, -0.08, 0);
        this.sprintPos.set(0.14, -0.18, -0.06);
        this.sprintRot.set(-0.7, 0.5, 0.1);
        this.adsPos.set(0.0, -0.04, -0.12);
        this.adsRot.set(0, 0, 0);
        break;
    }
    this.mesh.position.copy(this.holdPos);
    this.mesh.rotation.set(this.holdRot.x, this.holdRot.y, this.holdRot.z);
  }

  canFire(): boolean {
    const now = performance.now();
    const fireInterval = 1000 / this.config.fireRate;
    return !this.isReloading && this.currentAmmo > 0 && now - this.lastFireTime >= fireInterval;
  }

  fire(): boolean {
    if (!this.canFire()) return false;

    this.lastFireTime = performance.now();
    this.currentAmmo--;

    // Muzzle flash
    this.muzzleFlashLight.intensity = 3;
    this.muzzleFlashTimer = 0.05;

    // Recoil animation
    this.mesh.position.z += 0.02;

    this.events.emit('weapon:fire', { weapon: this });

    // Auto reload when empty
    if (this.currentAmmo <= 0) {
      this.reload();
    }

    return true;
  }

  reload(): void {
    if (this.isReloading || this.currentAmmo === this.getMaxAmmo()) return;
    this.isReloading = true;
    this.reloadTimer = this.getReloadTime();
    this.events.emit('weapon:reload', { weapon: this });
  }

  update(dt: number): void {
    // Reload timer
    if (this.isReloading) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        this.currentAmmo = this.getMaxAmmo();
        this.isReloading = false;
        this.events.emit('weapon:reloadComplete', { weapon: this });
      }
    }

    // Muzzle flash fade
    if (this.muzzleFlashTimer > 0) {
      this.muzzleFlashTimer -= dt;
      if (this.muzzleFlashTimer <= 0) {
        this.muzzleFlashLight.intensity = 0;
      }
    }

    // Weapon position: ADS → center, sprint → lowered, normal → hip
    const targetPos = this.isADS ? this.adsPos : (this.isSprinting ? this.sprintPos : this.holdPos);
    const targetRot = this.isADS ? this.adsRot : (this.isSprinting ? this.sprintRot : this.holdRot);
    const lerpSpeed = this.isADS ? dt * 12 : dt * 8; // ADS snaps faster
    this.mesh.position.x = THREE.MathUtils.lerp(this.mesh.position.x, targetPos.x, lerpSpeed);
    this.mesh.position.y = THREE.MathUtils.lerp(this.mesh.position.y, targetPos.y, lerpSpeed);
    this.mesh.position.z = THREE.MathUtils.lerp(this.mesh.position.z, targetPos.z, lerpSpeed);
    this.mesh.rotation.x = THREE.MathUtils.lerp(this.mesh.rotation.x, targetRot.x, lerpSpeed);
    this.mesh.rotation.y = THREE.MathUtils.lerp(this.mesh.rotation.y, targetRot.y, lerpSpeed);
    this.mesh.rotation.z = THREE.MathUtils.lerp(this.mesh.rotation.z, targetRot.z, lerpSpeed);
  }

  getDamage(): number {
    const idx = this.level - 1;
    const multiplier = UPGRADE.DAMAGE_MULTIPLIERS[idx] ?? 1;
    return this.config.damage * multiplier;
  }

  getReloadTime(): number {
    const idx = this.level - 1;
    const multiplier = UPGRADE.RELOAD_MULTIPLIERS[idx] ?? 1;
    return this.config.reloadTime * multiplier;
  }

  getMaxAmmo(): number {
    let capacity = this.config.magazineSize;
    if (this.level >= 5) capacity += Math.floor(this.config.magazineSize * 0.2);
    if (this.level >= 10) capacity += Math.floor(this.config.magazineSize * 0.3);
    return capacity;
  }

  getReloadProgress(): number {
    if (!this.isReloading) return 1;
    const totalTime = this.getReloadTime();
    return 1 - (this.reloadTimer / totalTime);
  }

  attachToCamera(camera: THREE.PerspectiveCamera): void {
    camera.add(this.mesh);
  }

  detachFromCamera(camera: THREE.PerspectiveCamera): void {
    camera.remove(this.mesh);
  }
}
